import React from 'react';

import { logQueryError } from '@helpers/query/Client';
import { useAppQueryCache } from '@helpers/query/Hooks';
import { AppQueryKey } from '@helpers/query/Keys';

/** Receives subscription data and errors from realtime backends. */
export type AppQuerySubscriptionObserver<TData> = {
  onData: (data: TData) => void;
  onError: (error: Error) => void;
};

/** Starts a realtime subscription and returns its unsubscribe function. */
export type AppQuerySubscribe<TData> = (
  observer: AppQuerySubscriptionObserver<TData>
) => VoidFunction;

export type AppQuerySubscriptionOptions<TData> = {
  /** Set false when auth or route params are not ready. */
  enabled?: boolean;
  /** Optional initial cache value before the subscription emits. */
  initialData?: TData;
  queryKey: AppQueryKey;
  subscribe: AppQuerySubscribe<TData>;
};

export function useAppQuerySubscription<TData>({
  enabled = true,
  initialData,
  queryKey,
  subscribe,
}: AppQuerySubscriptionOptions<TData>) {
  const queryCache = useAppQueryCache();

  React.useEffect(() => {
    if (!enabled) return;

    if (initialData !== undefined && queryCache.getQueryData<TData>(queryKey) === undefined) {
      queryCache.setQueryData(queryKey, initialData);
    }

    return subscribe({
      onData: (data) => queryCache.setQueryData(queryKey, data),
      onError: (error) => logQueryError(error, { queryKey }),
    });
  }, [enabled, initialData, queryCache, queryKey, subscribe]);
}
